import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { listingsAPI } from "../api/index.js";
import { useAuth } from "../hooks/useAuth.jsx";
import SEO from "../components/SEO.jsx";

const G = {
  bg:"#0A0A0A", surface:"#111111", border:"#1E1E1E",
  text:"#E4E4DC", muted:"#666", accent:"#00C896",
  purple:"#7C6FFF", danger:"#FF6B6B", warn:"#FFB347",
  font:"'Sora', sans-serif", mono:"'DM Mono', monospace",
};

const CATEGORIES = ["All","Forex Broker","Crypto Exchange","Stock Broker","Investment Platform","Other"];

const Stars = ({ rating, size=14 }) => {
  const r = Math.round(rating || 0);
  return (
    <span style={{ fontSize:size }}>
      {[1,2,3,4,5].map(i => (
        <span key={i} style={{ color: i<=r ? "#FFB347" : "#2A2A2A" }}>★</span>
      ))}
    </span>
  );
};

const RegBadge = ({ status }) => {
  const colors = { regulated:"#00C896", partial:"#FFB347", unregulated:"#FF6B6B", unverified:"#666" };
  const color = colors[status] || "#666";
  return (
    <span style={{ background:color+"22", border:`1px solid ${color}44`, color, fontSize:10, padding:"2px 8px", borderRadius:20, fontFamily:G.mono, letterSpacing:0.5 }}>
      {status?.toUpperCase()}
    </span>
  );
};

export default function ListingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [category, setCategory]   = useState("All");
  const [search, setSearch]       = useState("");
  const [sort, setSort]           = useState("rating");
  const [total, setTotal]         = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const params = { sort, limit:20 };
      if (category !== "All") params.category = category;
      if (search.trim()) params.search = search;
      const res = await listingsAPI.list(params);
      setListings(res.data.listings);
      setTotal(res.data.total);
    } catch {}
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [category, sort]);

  const handleSearch = (e) => { e.preventDefault(); load(); };

  return (
    <div className="listings-page" style={{ maxWidth:980, margin:"0 auto", padding:"28px 16px", fontFamily:G.font }}>
      <SEO
        title="Broker & Exchange Listings — Reviews & Scam Reports"
        description="Browse and review forex brokers, crypto exchanges, and investment platforms. Read real user reviews, complaints, and scam reports. Find regulated brokers before you invest."
        url="/listings"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          "name": "Broker & Exchange Listings",
          "description": "Browse forex brokers, crypto exchanges, and investment platforms. Read real user reviews and scam reports.",
          "url": "https://askfogra.com/listings"
        }}
      />

      {/* Header */}
      <div style={{ marginBottom:28 }}>
        <div style={{ fontSize:11, color:G.accent, fontFamily:G.mono, letterSpacing:2, textTransform:"uppercase", marginBottom:6 }}>Directory</div>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
          <h1 style={{ fontSize:28, fontWeight:700, color:G.text, margin:0 }}>Listings</h1>
          {user && <button onClick={() => navigate("/listings/new")} style={{ background:G.accent, border:"none", color:"#000", padding:"9px 22px", borderRadius:24, cursor:"pointer", fontFamily:G.mono, fontWeight:600, fontSize:13 }}>+ Submit Listing</button>}
        </div>
        <p style={{ color:G.muted, fontSize:14, marginTop:8 }}>Browse brokers, exchanges and platforms. Read real reviews and scam reports.</p>
      </div>

      {/* Search + Filter */}
      <div style={{ display:"flex", gap:12, marginBottom:20, flexWrap:"wrap" }}>
        <form onSubmit={handleSearch} style={{ display:"flex", gap:8, flex:1 }}>
          <input placeholder="Search listings..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex:1, background:"#141414", border:`1px solid #2A2A2A`, color:G.text, borderRadius:8, padding:"9px 14px", fontSize:13, fontFamily:G.font, outline:"none" }}
            onFocus={e => e.target.style.borderColor=G.accent}
            onBlur={e => e.target.style.borderColor="#2A2A2A"} />
          <button type="submit" style={{ background:G.accent, border:"none", color:"#000", padding:"9px 18px", borderRadius:8, cursor:"pointer", fontFamily:G.mono, fontSize:13, fontWeight:600 }}>Search</button>
        </form>
        <select value={sort} onChange={e => setSort(e.target.value)}
          style={{ background:"#141414", border:`1px solid #2A2A2A`, color:G.text, borderRadius:8, padding:"9px 14px", fontSize:13, fontFamily:G.font, outline:"none", cursor:"pointer" }}>
          <option value="rating">⭐ Top Rated</option>
          <option value="newest">🆕 Newest</option>
          <option value="complaints">🚨 Most Complaints</option>
        </select>
      </div>

      {/* Category Tabs */}
      <div style={{ display:"flex", gap:6, marginBottom:20, flexWrap:"wrap" }}>
        {CATEGORIES.map(c => (
          <button key={c} onClick={() => setCategory(c)} style={{
            background: category===c ? G.accent+"22" : "#141414",
            border: `1px solid ${category===c ? G.accent : "#2A2A2A"}`,
            color: category===c ? G.accent : G.muted,
            padding:"5px 14px", borderRadius:20, cursor:"pointer",
            fontSize:12, fontFamily:G.mono, transition:"all 0.15s"
          }}>{c}</button>
        ))}
      </div>

      <div style={{ fontSize:12, color:"#3A3A3A", fontFamily:G.mono, marginBottom:14 }}>{total} listings</div>

      {/* Listings Grid */}
      {loading ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:G.muted }}>Loading...</div>
      ) : listings.length === 0 ? (
        <div style={{ textAlign:"center", padding:"60px 0", color:"#333" }}>
          <div style={{ fontSize:36, marginBottom:10 }}>🔍</div>
          <div>No listings found</div>
        </div>
      ) : (
        <div className="listings-grid" style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(280px,1fr))", gap:16 }}>
          {listings.map(l => (
            <Link key={l.id} to={`/listings/${l.slug}`} style={{ textDecoration:"none" }}>
              <div style={{
                background:G.surface, border:`1px solid ${l.is_flagged ? G.danger : G.border}`,
                borderRadius:12, padding:20, height:"100%", boxSizing:"border-box",
                transition:"border-color 0.15s, transform 0.1s", cursor:"pointer"
              }}
                onMouseOver={e => { e.currentTarget.style.borderColor=l.is_flagged?G.danger:"#2A2A2A"; e.currentTarget.style.transform="translateY(-2px)"; }}
                onMouseOut={e => { e.currentTarget.style.borderColor=l.is_flagged?G.danger:G.border; e.currentTarget.style.transform="translateY(0)"; }}>

                {/* Logo placeholder + name */}
                <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
                  <div style={{ width:44, height:44, borderRadius:10, background:"#1A1A1A", border:`1px solid #2A2A2A`, display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:700, color:G.accent, fontFamily:G.mono, flexShrink:0 }}>
                    {l.name.slice(0,1)}
                  </div>
                  <div>
                    <div style={{ display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                      <span style={{ fontSize:15, fontWeight:600, color:G.text }}>{l.name}</span>
                      {l.is_verified && <span style={{ fontSize:10, color:G.accent }}>✓</span>}
                      {l.is_flagged && <span style={{ fontSize:10, color:G.danger }}>⚠️</span>}
                    </div>
                    <div style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>{l.category}</div>
                  </div>
                </div>

                {/* Description */}
                <p style={{ fontSize:13, color:G.muted, lineHeight:1.5, marginBottom:12, overflow:"hidden", display:"-webkit-box", WebkitLineClamp:2, WebkitBoxOrient:"vertical" }}>{l.description}</p>

                {/* Rating */}
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                  <Stars rating={l.avg_rating} />
                  <span style={{ fontSize:12, color:G.muted, fontFamily:G.mono }}>
                    {l.avg_rating ? l.avg_rating.toFixed(1) : "No ratings"} ({l.review_count})
                  </span>
                </div>

                {/* Footer */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:6 }}>
                  <RegBadge status={l.regulation_status} />
                  <div style={{ display:"flex", gap:10 }}>
                    {l.country && <span style={{ fontSize:11, color:"#444", fontFamily:G.mono }}>📍 {l.country}</span>}
                    {l.complaint_count > 0 && <span style={{ fontSize:11, color:G.danger, fontFamily:G.mono }}>🚨 {l.complaint_count}</span>}
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
